import React from "react";

import { InfoSmIcon as InfoSm, Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { Button, IconControl } from "ds/Button";
import Dialog from "ds/Dialog";
import Modal from "ds/Modal";
import * as Sticker from "ds/Sticker";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { goToEnvironment } from "Libs/utils";
import {
  environmentSelector,
  environmentLoadingSelector
} from "Reducers/environment";
import { branchActionStateSelector } from "Reducers/environment/actions/branch";
import { nonInactiveEnvironmentsSelector } from "Reducers/environment/environment.selectors";
import { gitProjectSelector } from "Reducers/project/project";
import { useAppSelector } from "Store/hooks";

import BranchContent from "./BranchContent";
import BranchContentSizing from "./BranchContentSizing";
import * as S from "./BranchPane.styles";

const ENVIRONMENT_LIMIT_ERROR_CODES = [402];

type Props = {
  isSizingApiEnabled?: boolean;
};

const EnvironmentBranchPane = ({ isSizingApiEnabled }: Props) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const { organizationId, projectId, environmentId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
    environmentId: string;
  }>();

  const nonInactiveEnvironments = useSelectorWithUrlParams(
    nonInactiveEnvironmentsSelector
  ).length;

  const project = useSelectorWithUrlParams(gitProjectSelector);

  const { loading: isLoading, errors } = useAppSelector(
    branchActionStateSelector
  );
  const environment = useSelectorWithUrlParams(environmentSelector);

  const isEnvironmentLoading = useSelectorWithUrlParams(
    environmentLoadingSelector
  );

  const hasEnvLimitError =
    nonInactiveEnvironments >= project?.subscription?.environments!;

  const isEnvLimitReached =
    hasEnvLimitError ||
    ENVIRONMENT_LIMIT_ERROR_CODES.includes(errors?.code as number);

  const isBranchContent =
    typeof isSizingApiEnabled !== "undefined" && !isSizingApiEnabled;

  const useSmallModalForErrorContent = isBranchContent && isEnvLimitReached;

  const onClose = () => {
    goToEnvironment(navigate, organizationId, projectId, environmentId);
  };

  const modalTitle = (
    <Typography.Title tag="h3" id="environment-branch-modal-title">
      {intl.formatMessage(
        { id: "branching_from" },
        { environment: environment?.title }
      )}
    </Typography.Title>
  );

  const cliDialog = (
    <Dialog
      side="top"
      title={intl.formatMessage({ id: "learnmore" })}
      description={intl.formatMessage(
        { id: "branch.dialog.cli.description" },
        {
          environmentName: environment?.title
        }
      )}
      analyticId="branch.dialog.cli.description"
      link={
        <Button
          analyticId="help_text"
          href={intl.formatMessage({
            id: "links.documentation.help.CLI"
          })}
          variant="link"
        >
          {intl.formatMessage({ id: "help_text", defaultMessage: "Help" })}
        </Button>
      }
      trigger={
        <IconControl>
          <InfoSm />
        </IconControl>
      }
    />
  );
  const BranchModalContent = isSizingApiEnabled
    ? BranchContentSizing
    : BranchContent;

  return (
    <Modal
      isOpen
      size={useSmallModalForErrorContent ? "small" : "medium"}
      onOpenChange={onClose}
      handleClose={onClose}
      id="environment_branch_modal_body"
    >
      {typeof isSizingApiEnabled === "undefined" ? (
        <S.LoadingPane />
      ) : (
        <div id="environment-branch-modal-body">
          {(errors?.message || errors?.detail) && (
            <S.StickerRoot priority="critical">
              <Sticker.Icon priority="critical" />
              {`${errors?.detail?.name ?? ""} ${errors?.detail?.error ?? ""} ${
                errors?.message ?? ""
              }`}
            </S.StickerRoot>
          )}

          <BranchModalContent
            isLoading={isLoading || isEnvironmentLoading}
            errors={errors}
            environment={environment}
            modalTitle={modalTitle}
            cliDialog={cliDialog}
            {...(isBranchContent && { hasEnvLimitError })}
          />
        </div>
      )}
    </Modal>
  );
};

export default EnvironmentBranchPane;
